/**
 * ModulService — à-la-carte modül erişim kapısı (entitlement).
 * Modül entitlement'ları abonelikler koleksiyonunda (veri.moduller[]) tutulur.
 * NOT: `kisitlama` varsayılan KAPALI → modulAcik hep true (mevcut erişim BOZULMAZ).
 * Faturalama devreye alınınca kisitlamaAc() ile sıkılaştırılır.
 */
import { Injectable, inject, signal } from '@angular/core';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { ModulId, KOMUT_ANAHTAR } from './moduller';
import { PaketService } from './paket.service';
import { IErisimAyar, etkinSeviye, seviyeBedavaMi } from './paketler';

const DB_AGGREGATE = 'FikoYT.sistem.DB.hareketler.aggregate';
const VT_ABONELIK = 'FikoYT.sistem.abonelikler';

@Injectable({ providedIn: 'root' })
export class ModulService {
  private FikoYT = inject(FikoYTService);
  private req = inject(FikoYTRequestService);
  private paket = inject(PaketService);

  /** Kalem erişim seviyeleri (kampanya/ücretsiz). */
  private seviyeler = signal<Record<string, IErisimAyar>>({});
  /** wbId veya "wbId:komutId" şu an ücretsiz mi? */
  private bedavaMi(anahtar: string): boolean {
    const sv = etkinSeviye(this.seviyeler()[anahtar], new Date().toISOString());
    if (sv === 'uye') return !!this.aktifId();
    return seviyeBedavaMi(sv);
  }

  constructor() {
    // Lib (3D motoru) → app workbench/komut lisans köprüsü.
    // Lib: window.__fikoModul('OLCUM') = workbench; window.__fikoModul('OLCUM','kumpas') = komut.
    // Köprü yoksa veya kısıtlama kapalıysa AÇIK (geriye uyumlu).
    try {
      (window as any).__fikoModul = (wb: any, komut?: string) => komut ? this.komutAcik(wb, komut) : this.workbenchAcik(wb);
      this.kullaniciYukle();
      this.paket.erisimSeviyeleri().then(s => this.seviyeler.set(s || {})).catch(() => { });
    } catch { }
  }

  /** Faturalama/kısıtlama aktif mi (varsayılan kapalı → her şey açık). */
  kisitlama = signal(false);
  aktifModuller = signal<Set<ModulId>>(new Set());     // açık workbench'ler
  aktifKomutlar = signal<Set<string>>(new Set());      // açık premium komutlar ("wbId:komutId")

  private aktifId(): string { return this.FikoYT.kimlik?.aktifKullaniciJson?.()?._id || ''; }
  private superMi(): boolean {
    const y = this.FikoYT.kimlik?.aktifKullaniciJson?.()?.yetkiler || [];
    return Array.isArray(y) && y.some((x: string) => x === 'Süper Yönetici' || x === 'Super Yonetici');
  }

  /** Workbench (modül) açık mı? Kısıtlama kapalıysa veya süper yöneticiyse hep açık. */
  modulAcik(id: ModulId): boolean {
    if (this.bedavaMi(id)) return true;                  // kampanya/ücretsiz
    if (!this.kisitlama()) return true;
    if (this.superMi()) return true;
    return this.aktifModuller().has(id);
  }
  /** workbenchAcik = modulAcik (workbench terminolojisi). */
  workbenchAcik(id: ModulId): boolean { return this.modulAcik(id); }

  /** Workbench içindeki premium komut açık mı? */
  komutAcik(wbId: ModulId, komutId: string): boolean {
    if (this.bedavaMi(KOMUT_ANAHTAR(wbId, komutId))) return true;   // kampanya/ücretsiz
    if (!this.kisitlama()) return true;
    if (this.superMi()) return true;
    return this.aktifKomutlar().has(KOMUT_ANAHTAR(wbId, komutId));
  }

  /** Kullanıcı bazlı entitlement (admin atadığı workbench + komutlar — goUyeler.veri). */
  async kullaniciYukle(): Promise<void> {
    const uid = this.aktifId(); if (!uid) return;
    try {
      const r = await this.req.aggregate<any>('FikoYT.sistem.DB.goUyeler.aggregate', [
        { $match: { _id: uid } }, { $project: { 'veri.workbenchler': 1, 'veri.komutlar': 1 } }, { $limit: 1 },
      ]);
      const wb = r?.[0]?.veri?.workbenchler || []; const km = r?.[0]?.veri?.komutlar || [];
      const s = new Set(this.aktifModuller()); for (const w of wb) s.add(w); this.aktifModuller.set(s);
      this.aktifKomutlar.set(new Set(km));
    } catch { /* sessiz */ }
  }

  /** Kullanıcı/firma entitlement'larını yükle (abonelikler.veri.moduller). */
  async yukle(firmaId?: string): Promise<void> {
    try {
      const match: any = { vt: VT_ABONELIK, durum: { $in: ['aktif', 'deneme'] }, silindi: { $ne: true } };
      if (firmaId) match['veri.firmaId'] = firmaId;
      const r = await this.req.aggregate<any>(DB_AGGREGATE, [{ $match: match }, { $project: { 'veri.moduller': 1 } }]);
      const set = new Set<ModulId>();
      for (const a of r) for (const m of (a?.veri?.moduller || [])) set.add(m);
      this.aktifModuller.set(set);
    } catch { /* sessiz — kısıtlama kapalıyken zaten gerekmez */ }
  }

  kisitlamaAc() { this.kisitlama.set(true); }
}
